import { processThresholds } from "./alerts"
import {
   LevelAlertConfigurationResponse,
   LevelSettingResponse,
   LevelSettingType,
} from "@/api/FacilityLevelsService"
import { IconProps } from "@/components/shared/Icon"
import { LEVEL_NAME_SEPARATOR } from "@/pages/Settings/OpenCellSchedules/constants"
import { AppDispatch } from "@/store"
import {
   resetFacilityDetails,
   resetFacilityLevelSelectors,
} from "@/store/facilitiesSlice"
import { resetLevelsConfigurations } from "@/store/levelConfigurationsSlice"
import {
   FacilityDetails,
   FacilityLevelDropdown,
   FacilityLevelType,
   LevelAlertConfiguration,
} from "@/types/facility"
import { Token } from "@/types/index"
import { LevelConfigurationItemMode } from "@/types/levelConfiguration"

export function formatAlertLevelConfiguration(
   alertConfiguration: LevelAlertConfigurationResponse
): LevelAlertConfiguration {
   return {
      id: alertConfiguration.id,
      source: alertConfiguration.source,
      priority: alertConfiguration.priority,
      name: alertConfiguration.name,
      colorCard: alertConfiguration.color.card.toLocaleLowerCase(),
      colorTypeface: alertConfiguration.color.typeface.toLocaleLowerCase(),
      soundFrequency: alertConfiguration.sound.frequency,
      soundClip: alertConfiguration.sound.clip,
      category: alertConfiguration.category,
      sensorTypes: alertConfiguration.sensorTypes,
      enabled: alertConfiguration.enabled,
      ...processThresholds(alertConfiguration.thresholds),
   } as unknown as LevelAlertConfiguration
}

export function buildLevelOptions(
   levels: FacilityDetails["levels"],
   parentId: string | null
) {
   return levels
      .filter((level) => {
         if (parentId === null) {
            return level.parents.length === 0
         }

         return level.parents?.includes(parentId) ?? false
      })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((level) => ({
         label: level.name,
         value: level.id,
         icon: level.type.icon as IconProps["name"],
      }))
}

export function getLevelLineage(
   levels: FacilityDetails["levels"],
   levelId: string
): FacilityDetails["levels"] {
   const level = levels.find(({ id }) => id === levelId)
   if (!level) return []

   return [level, ...getLevelLineage(levels, level.parents[0])]
}

export function getLevelPathName(
   levels: FacilityDetails["levels"],
   levelId: string
): string {
   return getLevelLineage(levels, levelId)
      .map(({ name }) => name)
      .reverse()
      .join(LEVEL_NAME_SEPARATOR)
}

export function buildLevelSelectors(
   levelTypes: FacilityLevelType[],
   levels: FacilityDetails["levels"],
   persistedActiveValues: Record<string, string> = {}
) {
   const sortedLevelTypes = [...levelTypes]
      .filter((type) => type.mode === LevelConfigurationItemMode.PHYSICAL)
      .sort((a, b) => a.depth - b.depth)
      .slice(0, -1)
      .map((type) => {
         return type.name
      })
   const levelSelectors = sortedLevelTypes.reduce((acc, levelTypeName, i) => {
      const parentId = i === 0 ? null : acc[i - 1].activeValue

      const options = buildLevelOptions(levels, parentId)
      const typeId = levelTypes.find((lt) => lt.name === levelTypeName)!.id
      const persistedValue = persistedActiveValues[typeId]
      const activeOption =
         options.find((option) => option.value === persistedValue) ?? options[0]

      acc.push({
         typeId,
         label: levelTypeName,
         activeValue: activeOption?.value,
         options,
         icon: activeOption?.icon,
      })

      return acc
   }, [] as FacilityLevelDropdown[])
   return levelSelectors
}

export function getPersistedLevelSelectors(): Record<string, string> {
   try {
      const activeSelectorsValues = sessionStorage.getItem(
         Token.FACILITY_LEVEL_SELECTORS
      )
      return activeSelectorsValues ? JSON.parse(activeSelectorsValues) : {}
   } catch (error) {
      console.error("Failed to parse persisted level selectors:", error)
      return {}
   }
}

export function persistLevelSelectors(levelSelectors: FacilityLevelDropdown[]) {
   const activeValues = levelSelectors.reduce(
      (acc, selector) => {
         acc[selector.typeId] = selector.activeValue
         return acc
      },
      {} as Record<string, string>
   )
   sessionStorage.setItem(
      Token.FACILITY_LEVEL_SELECTORS,
      JSON.stringify(activeValues)
   )
}

export function formatLevelSettings(settings: LevelSettingResponse[]) {
   return settings.reduce(
      (acc, setting) => {
         acc[setting.key as LevelSettingType] = setting.value
         return acc
      },
      {} as Record<string, string | boolean | number | null>
   )
}

export function resetAllFacilityData(dispatch: AppDispatch) {
   dispatch(resetFacilityDetails())
   dispatch(resetFacilityLevelSelectors())
   dispatch(resetLevelsConfigurations())
}
